import { useEffect, useRef, useCallback } from 'react';

/**
 * Retorna uma função `getSignal()` que devolve um AbortSignal vivo,
 * automaticamente abortado quando o componente desmontar.
 *
 * Uso:
 *   const getSignal = useAbortSignal();
 *
 *   useEffect(() => {
 *     getDataDashboard(id, type, getSignal())
 *       .then(...)
 *       .catch((err) => { if (!isAbortError(err)) notifyError(...); });
 *   }, [id]);
 *
 * A função `getSignal()` cria um novo AbortController na primeira chamada
 * dentro de cada render-cycle (após reset). Para forçar reset entre fetches
 * sequenciais, use `resetSignal()`.
 */
export const useAbortSignal = () => {
  const controllerRef = useRef(null);

  useEffect(
    () => () => {
      if (controllerRef.current) controllerRef.current.abort();
    },
    []
  );

  const getSignal = useCallback(() => {
    if (!controllerRef.current) {
      controllerRef.current = new AbortController();
    }
    return controllerRef.current.signal;
  }, []);

  const resetSignal = useCallback(() => {
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = new AbortController();
    return controllerRef.current.signal;
  }, []);

  return { getSignal, resetSignal };
};

export default useAbortSignal;
